<template>
  <div class="award-group-editor">
    <div class="award-group-editor__table-wrap">
      <table class="award-group-editor__table">
        <thead>
          <tr>
            <th class="award-group-editor__select-col award-group-editor__pinned">
              <b-form-checkbox
                :checked="allSelected"
                :indeterminate="someSelected && !allSelected"
                @change="toggleAll"
              />
            </th>
            <template v-for="col in orderedColumns">
              <th
                :key="col.key"
                :class="{
                  'award-group-editor__toggles-col': col.toggles,
                  'award-group-editor__pinned': isPinnedKey(col.key),
                }"
                :style="col.toggles ? pinnedStyle(col.key) : cellStyle(col.field)"
              >
                <div class="award-group-editor__th">
                  <span class="award-group-editor__th-label">
                    {{ col.toggles ? 'Optional toggles' : col.field.label }}
                  </span>
                  <!-- Pinned column: a pin icon that unpins on click. Others: a ⋮ menu to pin. -->
                  <button
                    v-if="isPinnedKey(col.key)"
                    type="button"
                    class="award-group-editor__pin-btn"
                    title="Unpin column"
                    @click="togglePinKey(col.key)"
                  >
                    <font-awesome-icon :icon="['fas', 'thumbtack']" />
                  </button>
                  <b-dropdown
                    v-else
                    no-caret
                    variant="link"
                    size="sm"
                    toggle-class="award-group-editor__col-menu-btn"
                    menu-class="award-group-editor__col-menu"
                  >
                    <template #button-content>
                      <font-awesome-icon :icon="['fas', 'ellipsis-vertical']" />
                    </template>
                    <b-dropdown-item @click="togglePinKey(col.key)">
                      <font-awesome-icon :icon="['fas', 'thumbtack']" class="mr-2" />Pin
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </th>
            </template>
            <th v-if="mode === 'create'" class="award-group-editor__action-col"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in localRows" :key="index">
            <td class="award-group-editor__select-col award-group-editor__pinned">
              <b-form-checkbox :checked="isRowSelected(index)" @change="() => toggleRow(index)" />
            </td>
            <template v-for="col in orderedColumns">
              <td
                v-if="col.toggles"
                :key="col.key"
                class="award-group-editor__toggles-col"
                :class="{ 'award-group-editor__pinned': isPinnedKey(TOGGLES_KEY) }"
                :style="pinnedStyle(TOGGLES_KEY)"
              >
                <div class="award-group-editor__toggles">
                  <b-form-checkbox
                    v-for="t in featureToggles"
                    :key="t.key"
                    switch
                    class="award-group-editor__toggle"
                    :checked="Boolean(row[t.key])"
                    :disabled="
                      (t.requiresMonetary && !row.monetary_enabled) ||
                      (t.requiresMultipleBannerImages && (row.banner_s3_keys || []).length < 2)
                    "
                    @change="value => onToggleChange(index, t, value)"
                  >
                    {{ t.label }}
                  </b-form-checkbox>
                </div>
              </td>
              <td
                v-else
                :key="col.key"
                :class="{
                  'award-group-editor__pinned': isPinned(col.field),
                  'award-group-editor__cell--accent':
                    multiSelectActive &&
                    isRowSelected(index) &&
                    (activeColumn === col.field.key || hoverColumn === col.field.key),
                  'award-group-editor__cell--invalid': showErrors && cellInvalid(col.field, row),
                }"
                :style="cellStyle(col.field)"
                @focusin="onCellFocus(col.field.key, index)"
                @focusout="onCellBlur"
                @mouseenter="onCellHover(col.field.key, index)"
                @mouseleave="onCellHoverOut(col.field.key)"
              >
                <template v-if="isDisabled(col.field, row)">
                  <span class="text-muted">—</span>
                </template>
                <template v-else-if="isLocked(col.field, row)">
                  <span v-b-tooltip.hover :title="LOCKED_REASON" class="award-group-editor__locked">
                    {{ lockedDisplay(col.field, row) }}
                    <font-awesome-icon :icon="['fal', 'lock']" class="ml-1" />
                  </span>
                </template>
                <template v-else-if="col.field.type === 'text'">
                  <b-form-textarea
                    :value="row[col.field.key]"
                    size="sm"
                    rows="1"
                    max-rows="6"
                    no-resize
                    @input="value => setCell(index, col.field.key, value)"
                  />
                </template>
                <template v-else-if="col.field.type === 'toggle'">
                  <b-form-checkbox
                    :checked="row[col.field.key]"
                    switch
                    @change="value => setCell(index, col.field.key, value)"
                  />
                </template>
                <template v-else-if="col.field.type === 'select'">
                  <b-form-select
                    :value="row[col.field.key]"
                    size="sm"
                    :options="selectOptions(col.field)"
                    @change="value => setCell(index, col.field.key, value)"
                  />
                </template>
                <template v-else-if="col.field.type === 'currency'">
                  <b-input-group
                    v-if="!Array.isArray(row[col.field.key])"
                    size="sm"
                    class="award-group-editor__currency"
                  >
                    <b-input-group-prepend is-text>{{ currencySymbol }}</b-input-group-prepend>
                    <b-form-input
                      :value="minorToMajor(row[col.field.key])"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      @input="value => setCell(index, col.field.key, majorToMinor(value))"
                    />
                  </b-input-group>
                  <span v-else class="text-muted">multiple values</span>
                </template>
                <template v-else-if="col.field.type === 'number'">
                  <b-form-input
                    :value="row[col.field.key]"
                    type="number"
                    size="sm"
                    @input="value => setCell(index, col.field.key, numericValue(value))"
                  />
                </template>
                <template v-else-if="col.field.type === 'budget'">
                  <b-input-group size="sm" class="award-group-editor__budget">
                    <b-input-group-prepend is-text>{{ currencySymbol }}</b-input-group-prepend>
                    <b-form-input
                      :value="minorToMajor(row.budget_minor)"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      @input="value => setCell(index, 'budget_minor', majorToMinor(value))"
                    />
                    <b-input-group-append is-text>Per</b-input-group-append>
                    <b-form-select
                      :value="row.budget_cadence"
                      :options="budgetCadenceOptions"
                      @change="value => setCell(index, 'budget_cadence', value)"
                    />
                  </b-input-group>
                </template>
                <template v-else-if="col.field.type === 'duration-months'">
                  <b-input-group size="sm" class="award-group-editor__duration">
                    <b-form-input
                      :value="row[col.field.key]"
                      type="number"
                      min="0"
                      placeholder="e.g. 12"
                      @input="value => setCell(index, col.field.key, value)"
                    />
                    <b-input-group-append is-text>mo</b-input-group-append>
                  </b-input-group>
                </template>
                <template v-else-if="col.field.type === 'multiselect'">
                  <multi-select
                    :id="`ms-${col.field.key}-${index}`"
                    :value="row[col.field.key]"
                    :options="col.field.options || []"
                    :searchable="false"
                    :select-all="false"
                    placeholder="Select"
                    @input="value => setCell(index, col.field.key, value)"
                  />
                </template>
                <template v-else-if="col.field.type === 'division-multiselect'">
                  <multi-select
                    :id="`ms-${col.field.key}-${index}`"
                    :value="row[col.field.key]"
                    :options="divisionOptions"
                    :searchable="true"
                    :search-min-chars="0"
                    search-placeholder="Search divisions..."
                    :select-all="true"
                    select-all-text="All divisions"
                    :empty-means-all="true"
                    placeholder="All divisions"
                    @input="value => setCell(index, col.field.key, value)"
                  />
                </template>
                <template v-else-if="col.field.type === 'employee-multiselect'">
                  <multi-select
                    :id="`ms-${col.field.key}-${index}`"
                    :value="row[col.field.key]"
                    :options="employeeOptions"
                    :searchable="true"
                    :select-all="false"
                    placeholder="Select employees"
                    @input="value => setCell(index, col.field.key, value)"
                  />
                </template>
                <template v-else-if="col.field.type === 'modal'">
                  <create-reward-amounts-modal
                    v-if="col.field.modal === 'amounts'"
                    :value="row.award_value_minor"
                    :default-minor="row.default_award_value_minor"
                    @input="value => setCell(index, 'award_value_minor', value)"
                    @update:defaultMinor="value => setCell(index, 'default_award_value_minor', value)"
                  />
                  <create-categories-modal
                    v-else-if="col.field.modal === 'categories'"
                    :value="row.categories"
                    @input="value => setCell(index, 'categories', value)"
                  />
                  <create-company-values-modal
                    v-else-if="col.field.modal === 'company-values'"
                    :value="row.company_values"
                    @input="value => setCell(index, 'company_values', value)"
                  />
                </template>
                <template v-else-if="col.field.type === 'image'">
                  <div class="award-group-editor__image-cell">
                    <div class="award-group-editor__image-preview-wrap">
                      <img
                        v-if="imagePreview(col.field, row) && !imageFailed(index, col.field.key)"
                        :src="imagePreview(col.field, row)"
                        :alt="col.field.label"
                        class="award-group-editor__image-preview"
                        :class="{
                          'award-group-editor__image-preview--badge': col.field.key === 'badge_s3_key',
                          'award-group-editor__image-preview--open-banner':
                            col.field.key === 'banner_s3_key' && isOpenAwardRow(row),
                        }"
                        @error="onImageError(index, col.field.key)"
                      />
                      <div
                        v-else
                        class="award-group-editor__image-placeholder"
                        :class="{
                          'award-group-editor__image-placeholder--badge': col.field.key === 'badge_s3_key',
                          'award-group-editor__image-placeholder--open-banner':
                            col.field.key === 'banner_s3_key' && isOpenAwardRow(row),
                        }"
                      >
                        <font-awesome-icon :icon="['far', 'image']" />
                      </div>
                      <span v-if="imageCount(col.field, row) >= 2" class="award-group-editor__image-count">
                        {{ imageCount(col.field, row) }}
                      </span>
                    </div>
                    <div class="award-group-editor__image-actions">
                      <b-button
                        v-b-modal="'bulk-image-upload'"
                        variant="link"
                        size="sm"
                        class="award-group-editor__image-btn"
                        @click="setImageTarget(index, imageKind(col.field))"
                      >
                        {{ imageKind(col.field) === 'banner' ? 'Upload images' : 'Upload image' }}
                      </b-button>
                      <b-button
                        v-b-modal="'bulk-image-gallery'"
                        variant="link"
                        size="sm"
                        class="award-group-editor__image-btn"
                        :disabled="!galleryHasImages(imageKind(col.field), row)"
                        @click="setImageTarget(index, imageKind(col.field))"
                      >
                        Choose from gallery
                      </b-button>
                      <b-button
                        v-if="imageCount(col.field, row) > 0"
                        variant="link"
                        size="sm"
                        class="award-group-editor__image-btn text-danger"
                        @click="clearBannerImages(index)"
                      >
                        Remove images
                      </b-button>
                    </div>
                  </div>
                </template>
              </td>
            </template>
            <td v-if="mode === 'create'" class="award-group-editor__action-col">
              <b-button
                variant="link"
                class="award-group-editor__delete-btn"
                :class="{
                  'award-group-editor__delete-btn--peer-hover':
                    deleteHover && multiSelectActive && isRowSelected(index),
                }"
                title="Delete award"
                @mouseenter="onDeleteHover(index, true)"
                @mouseleave="onDeleteHover(index, false)"
                @click="deleteRow(index)"
              >
                <font-awesome-icon :icon="['fal', 'trash']" />
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <button v-if="mode === 'create'" type="button" class="award-group-editor__add-row" @click="addRow">
      <font-awesome-icon :icon="['fal', 'plus']" class="mr-2" />
      Add award
    </button>

    <!-- Shared upload/gallery modals, targeted at the active image cell. Banner supports
         picking multiple images; badge stays single. -->
    <image-upload-modal
      modal-id="bulk-image-upload"
      :title="activeImageKind === 'badge' ? 'Add badge image' : 'Add banner images'"
      :preview-type="activeImageKind === 'badge' ? 'badge' : 'banner'"
      :multiple="activeImageKind === 'banner'"
      :supported-text="
        activeImageKind === 'badge'
          ? 'JPG or PNG • up to 10 MB • Recommended 300 × 300 px (1:1)'
          : activeImageApiType === 'open_banner'
          ? 'JPG or PNG • up to 10 MB • Recommended 1600 × 400 px (4:1)'
          : 'JPG or PNG • up to 10 MB • Recommended 1600 × 900 px (16:9)'
      "
      @update:file="onImageUploadFile"
      @update:files="onImageUploadFiles"
    />
    <image-gallery-modal
      modal-id="bulk-image-gallery"
      :image-type="activeImageApiType"
      :multiple="activeImageKind === 'banner'"
      :max-selectable="activeImageKind === 'banner' ? MAX_BANNER_IMAGES : 1"
      @select="onImageGallerySelect"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import ApiClient from '@/ApiClient';
import MultiSelect from '@/components/MultiSelect.vue';
import CreateRewardAmountsModal from '@/pages/recognition/components/CreateRewardAmountsModal.vue';
import CreateCategoriesModal from '@/pages/recognition/components/CreateCategoriesModal.vue';
import CreateCompanyValuesModal from '@/pages/recognition/components/CreateCompanyValuesModal.vue';
import ImageUploadModal from '@/pages/recognition/components/ImageUploadModal.vue';
import ImageGalleryModal from '@/pages/recognition/components/ImageGalleryModal.vue';
import { fieldsForType, featureTogglesForType } from '@/pages/recognition/awardFieldRegistry';
import useFeatureFlags from '@/composables/useFeatureFlags';
import {
  RECOGNITION_AWARD_TYPES,
  RECOGNITION_BUDGET_CADENCE_OPTIONS,
  MILESTONE_LOCKED_REASON as LOCKED_REASON,
} from '@/pages/recognition/constants';
import { currencySymbolFromCode } from '@/utils/common';
import State from '@/state/State';
import useToast from '@/composables/useToast';

const { doToast, TOAST_VARIANT } = useToast();

const props = defineProps({
  mode: { type: String, default: 'create' },
  programType: { type: String, required: true },
  rows: { type: Array, default: () => [] },
  divisionOptions: { type: Array, default: () => [] },
  employeeOptions: { type: Array, default: () => [] },
  // Seed for "Add award" when the table has been emptied — a copy of the original.
  rowTemplate: { type: Object, default: null },
  // When true, empty required cells are outlined in red (set after a failed submit).
  showErrors: { type: Boolean, default: false },
});

const emit = defineEmits(['input', 'validation']);

const localRows = ref(cloneRows(props.rows));

// Track the array reference we last emitted. The parent mirrors our `input`
// straight back into `props.rows` (`@input="rows = $event"`), so without this
// guard the props watcher would re-clone that echo into a new array, the deep
// watcher would emit again, and the two would loop forever (freezing the page
// on any edit). Ignoring our own echo breaks the cycle.
let lastEmitted = null;

watch(
  () => props.rows,
  value => {
    if (value === lastEmitted) {
      return;
    }
    localRows.value = cloneRows(value);
  },
);

watch(
  localRows,
  value => {
    lastEmitted = value;
    emit('input', value);
  },
  { deep: true },
);

const { recognitionPayTypeScopingEnabled } = useFeatureFlags();

const fields = computed(() =>
  fieldsForType(props.programType, { payTypeScopingEnabled: recognitionPayTypeScopingEnabled.value }),
);

// Optional-feature toggles shown in the leading "Optional toggles" column.
const featureToggles = computed(() => featureTogglesForType(props.programType));

// A gated column is shown only while at least one row actually has that cell enabled
// (its conditional passes). This also hides e.g. Reward amounts vs Max amount based
// on each row's amount type. Ungated columns (no conditional) always show.
const visibleFields = computed(() =>
  fields.value.filter(field => !field.conditional || localRows.value.some(row => field.conditional(row))),
);

function onToggleChange(index, toggle, value) {
  setCell(index, toggle.key, value);
}

// ── Required-field validation ────────────────────────────────────────────────
function isEmptyValue(value) {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
}

// A cell is invalid when it's a required field, enabled for this row, and empty.
// `required` may be a boolean or a (row) => boolean (e.g. approvers depend on type).
function isRequired(field, row) {
  return typeof field.required === 'function' ? field.required(row) : Boolean(field.required);
}
function cellInvalid(field, row) {
  // A locked cell can't be filled in, so flagging it would block a save the user can't fix.
  if (isDisabled(field, row) || isLocked(field, row)) {
    return false;
  }
  // Budget cell: entering an amount requires choosing a period.
  if (field.type === 'budget') {
    return !isEmptyValue(row.budget_minor) && !row.budget_cadence;
  }
  return isRequired(field, row) && isEmptyValue(row[field.key]);
}

// { count: total empty required cells, columns: distinct column labels affected }.
const validationSummary = computed(() => {
  // Budget cells are conditionally required (a period once an amount is set), so
  // validate them alongside the always-required fields.
  const required = fields.value.filter(field => field.required || field.type === 'budget');
  let count = 0;
  const columns = [];
  const seen = new Set();
  localRows.value.forEach(row => {
    required.forEach(field => {
      if (cellInvalid(field, row)) {
        count += 1;
        if (!seen.has(field.key)) {
          seen.add(field.key);
          columns.push(field.label);
        }
      }
    });
  });
  return { count, columns };
});
watch(validationSummary, summary => emit('validation', summary), { immediate: true, deep: true });

// ── Row selection / multi-edit ───────────────────────────────────────────────
// When more than one row is selected, editing any cell of a selected row applies
// the same value to every selected row.
const selectedIndices = ref(new Set());
const allSelected = computed(() => localRows.value.length > 0 && selectedIndices.value.size === localRows.value.length);
const someSelected = computed(() => selectedIndices.value.size > 0);
// Two or more selected rows edit/delete together; drives the linked-row highlight.
const multiSelectActive = computed(() => selectedIndices.value.size > 1);

// Hovering one delete in a multi-selection previews that the whole selection goes.
const deleteHover = ref(false);
function onDeleteHover(index, hovering) {
  if (multiSelectActive.value && isRowSelected(index)) {
    deleteHover.value = hovering;
  }
}

// Focusing a cell in the multi-selection accents that same column across every
// selected row, signalling that an edit there replicates down the column.
const activeColumn = ref(null);
function onCellFocus(key, index) {
  if (multiSelectActive.value && isRowSelected(index)) {
    activeColumn.value = key;
  }
}
function onCellBlur() {
  activeColumn.value = null;
}

// Hovering any cell in the multi-selection previews the same column-wide accent
// (covers components like the edit pill that aren't focusable).
const hoverColumn = ref(null);
function onCellHover(key, index) {
  if (multiSelectActive.value && isRowSelected(index)) {
    hoverColumn.value = key;
  }
}
function onCellHoverOut(key) {
  if (hoverColumn.value === key) {
    hoverColumn.value = null;
  }
}

function isRowSelected(index) {
  return selectedIndices.value.has(index);
}
function toggleRow(index) {
  const next = new Set(selectedIndices.value);
  if (next.has(index)) {
    next.delete(index);
  } else {
    next.add(index);
  }
  selectedIndices.value = next;
}
function toggleAll(checked) {
  selectedIndices.value = checked ? new Set(localRows.value.map((_, i) => i)) : new Set();
}

function numericValue(value) {
  if (value === '' || value === null || value === undefined) {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

const currencySymbol = computed(() => currencySymbolFromCode(State.state?.company?.default_currency));
const budgetCadenceOptions = [{ value: null, text: 'Select period' }, ...RECOGNITION_BUDGET_CADENCE_OPTIONS];

// budget_minor is stored in minor units; the cell edits it in major units.
function minorToMajor(minor) {
  if (minor === '' || minor === null || minor === undefined) {
    return '';
  }
  const n = Number(minor);
  return Number.isFinite(n) ? n / 100 : '';
}
function majorToMinor(value) {
  const n = parseFloat(String(value ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

// ── Column pinning ───────────────────────────────────────────────────────────
// Only one column can be pinned at a time. The pinned column (a data field OR the
// Optional toggles column) leads the order so it sits at the very left, right after
// the checkbox, and sticks there. The toggles column otherwise keeps its default
// slot ahead of the data columns.
const PINNED_LEFT = '2rem'; // checkbox column width
// Synthetic key so the Optional toggles column joins the same single-pin model as
// the data columns (it isn't a registry field).
const TOGGLES_KEY = '__optional_toggles__';
const pinned = ref(new Set(['program_name']));

function isPinnedKey(key) {
  return pinned.value.has(key);
}
function isPinned(field) {
  return isPinnedKey(field.key);
}

function togglePinKey(key) {
  // Pinning replaces any existing pin (one column at a time).
  pinned.value = pinned.value.has(key) ? new Set() : new Set([key]);
}

function pinnedStyle(key) {
  return isPinnedKey(key) ? { left: PINNED_LEFT } : null;
}
function cellStyle(field) {
  return { ...pinnedStyle(field.key), ...(field.minWidth ? { minWidth: field.minWidth } : {}) };
}

// Unified column order. A `{ toggles: true }` entry marks the Optional toggles
// column; `{ field }` entries are data columns.
const TOGGLES_COLUMN = { toggles: true, key: TOGGLES_KEY };
const orderedColumns = computed(() => {
  const cols = [];
  if (isPinnedKey(TOGGLES_KEY)) {
    cols.push(TOGGLES_COLUMN);
    visibleFields.value.forEach(field => cols.push({ field, key: field.key }));
  } else {
    const pinnedField = visibleFields.value.find(field => isPinnedKey(field.key));
    if (pinnedField) {
      cols.push({ field: pinnedField, key: pinnedField.key });
    }
    cols.push(TOGGLES_COLUMN);
    visibleFields.value.filter(field => !isPinnedKey(field.key)).forEach(field => cols.push({ field, key: field.key }));
  }
  return cols;
});

// Fall back to "—" when a preview image can't load (e.g. an S3 URL that doesn't
// resolve in local dev) rather than showing the browser's broken-image glyph.
const failedImages = ref(new Set());
function imageFailed(index, key) {
  return failedImages.value.has(`${index}-${key}`);
}
function onImageError(index, key) {
  const next = new Set(failedImages.value);
  next.add(`${index}-${key}`);
  failedImages.value = next;
}

function imagePreview(field, row) {
  if (field.key === 'banner_s3_key') {
    return (row.banner_presigned_urls || [])[0] || row.banner_presigned_url || '';
  }
  if (field.key === 'badge_s3_key') {
    return row.badge_presigned_url || '';
  }
  return '';
}

// Number of images held for this cell — only the banner supports more than one.
function imageCount(field, row) {
  if (field.key !== 'banner_s3_key') {
    return 0;
  }
  return (row.banner_s3_keys || []).length;
}

// ── Banner / badge image upload + gallery ────────────────────────────────────
// 'banner' uses gallery/upload pool 'banner' (program/milestone) or 'open_banner'
// (open awards, cropped to a different aspect ratio in the app); 'badge' uses 'logo'.
function imageKind(field) {
  return field.key === 'badge_s3_key' ? 'badge' : 'banner';
}

function isOpenAwardRow(row) {
  return row?.program_type === RECOGNITION_AWARD_TYPES.OPEN;
}

// The image_type sent to the backend / used to key the gallery pool.
function imageApiType(kind, row) {
  if (kind === 'badge') {
    return 'logo';
  }
  return isOpenAwardRow(row) ? 'open_banner' : 'banner';
}

// The upload/gallery modals are shared; this tracks which cell triggered them.
const activeImageTarget = ref({ index: null, kind: 'banner', apiType: 'banner' });
const activeImageKind = computed(() => activeImageTarget.value.kind);
const activeImageApiType = computed(() => activeImageTarget.value.apiType);
function setImageTarget(index, kind) {
  activeImageTarget.value = { index, kind, apiType: imageApiType(kind, localRows.value[index]) };
}

// Disable the gallery button when the company has no gallery images of that pool.
const galleryAvailable = ref({ banner: false, open_banner: false, badge: false });
function galleryHasImages(kind, row) {
  if (kind === 'badge') {
    return galleryAvailable.value.badge;
  }
  return isOpenAwardRow(row) ? galleryAvailable.value.open_banner : galleryAvailable.value.banner;
}
onMounted(async () => {
  try {
    const data = await ApiClient.getRecognitionGalleryImages();
    galleryAvailable.value = {
      banner: Object.keys(data?.generic?.banner || {}).length > 0,
      open_banner: Object.keys(data?.generic?.open_banner || {}).length > 0,
      badge: Object.keys(data?.generic?.logo || {}).length > 0,
    };
  } catch (e) {
    galleryAvailable.value = { banner: false, open_banner: false, badge: false };
  }
});

const MAX_BANNER_IMAGES = 9;

function setRowImage(index, kind, s3Key, url) {
  const keyField = kind === 'badge' ? 'badge_s3_key' : 'banner_s3_key';
  const urlField = kind === 'badge' ? 'badge_presigned_url' : 'banner_presigned_url';
  setCell(index, keyField, s3Key || null);
  setCell(index, urlField, url || '');
  // Clear any prior load-failure flag so the new image renders.
  const next = new Set(failedImages.value);
  next.delete(`${index}-${keyField}`);
  failedImages.value = next;
}

function setBannerImages(index, keys, urls) {
  setCell(index, 'banner_s3_keys', keys);
  setCell(index, 'banner_presigned_urls', urls);
  setCell(index, 'banner_s3_key', keys[0] || null);
  setCell(index, 'banner_presigned_url', urls[0] || '');
  const next = new Set(failedImages.value);
  next.delete(`${index}-banner_s3_key`);
  failedImages.value = next;
}

function appendBannerImages(index, keys, urls) {
  const row = localRows.value[index];
  if (!row) {
    return;
  }
  const remaining = Math.max(0, MAX_BANNER_IMAGES - (row.banner_s3_keys || []).length);
  setBannerImages(
    index,
    [...(row.banner_s3_keys || []), ...keys.slice(0, remaining)],
    [...(row.banner_presigned_urls || []), ...urls.slice(0, remaining)],
  );
}

function clearBannerImages(index) {
  setBannerImages(index, [], []);
}

async function onImageUploadFile(file) {
  const { index, kind, apiType } = activeImageTarget.value;
  if (index === null || !file) {
    return;
  }
  try {
    const result = await ApiClient.uploadRecognitionImage(file, apiType);
    setRowImage(index, kind, result?.s3_key, result?.presigned_url);
  } catch (e) {
    // Leave the cell unchanged; the modal closes silently otherwise, so tell the user.
    doToast('Upload failed', 'Could not upload the image. Please try again.', TOAST_VARIANT.DANGER);
  }
}

async function onImageUploadFiles(files) {
  const { index, apiType } = activeImageTarget.value;
  if (index === null || !files?.length) {
    return;
  }
  const results = await Promise.allSettled(files.map(file => ApiClient.uploadRecognitionImage(file, apiType)));
  const uploaded = results.filter(r => r.status === 'fulfilled' && r.value?.s3_key).map(r => r.value);
  if (uploaded.length) {
    appendBannerImages(
      index,
      uploaded.map(u => u.s3_key),
      uploaded.map(u => u.presigned_url),
    );
  }
  if (uploaded.length < files.length) {
    doToast('Upload failed', 'Some images could not be uploaded. Please try again.', TOAST_VARIANT.DANGER);
  }
}

function onImageGallerySelect(selection) {
  const { index, kind } = activeImageTarget.value;
  if (index === null) {
    return;
  }
  if (kind === 'banner' && Array.isArray(selection)) {
    appendBannerImages(
      index,
      selection.map(s => s.s3Key),
      selection.map(s => s.url),
    );
    return;
  }
  const { url, s3Key } = Array.isArray(selection) ? selection[0] || {} : selection;
  setRowImage(index, kind, s3Key, url);
}

function cloneRows(rows) {
  return (rows || []).map(row => ({ ...row }));
}

function selectOptions(field) {
  // Amount type is a binary mode (Fixed/Adjustable) — no empty choice.
  if (field.key === 'amount_mode') {
    return field.options || [];
  }
  return [{ text: '—', value: null }, ...(field.options || [])];
}

function isDisabled(field, row) {
  if (field.readonly) {
    return false;
  }
  return typeof field.conditional === 'function' && !field.conditional(row);
}

function isLocked(field, row) {
  return typeof field.locked === 'function' && field.locked(row);
}

function lockedDisplay(field, row) {
  const value = row[field.key];
  if (isEmptyValue(value)) {
    return '—';
  }
  const option = (field.options || []).find(opt => opt.value === value);
  return option ? option.text : value;
}

function setCell(index, key, value) {
  // Replicate to every selected row when editing within a multi-selection.
  const targets = isRowSelected(index) && selectedIndices.value.size > 1 ? [...selectedIndices.value] : [index];
  for (const i of targets) {
    // Clone arrays so rows don't share a mutable reference.
    localRows.value[i][key] = Array.isArray(value) ? [...value] : value;
  }
}

// Append a new award copy, seeded from the first row — or, when the table has been
// emptied, from the original award template so it's never a blank row.
function addRow() {
  const src = localRows.value[0] || props.rowTemplate || {};
  const base = {};
  for (const [key, value] of Object.entries(src)) {
    base[key] = Array.isArray(value) ? [...value] : value;
  }
  base.recognition_award_program_id = null;
  // The seed row may have been awarded; the new copy has no history, so nothing is locked.
  base.has_been_awarded = false;
  localRows.value.push(base);
}

function deleteRow(index) {
  // Deleting a selected row while multiple are selected removes the whole
  // selection (matching the multi-edit behaviour the highlight signals).
  const targets = multiSelectActive.value && isRowSelected(index) ? new Set(selectedIndices.value) : new Set([index]);
  localRows.value = localRows.value.filter((_, i) => !targets.has(i));
  // Row indices shift after removal, so drop any stale multi-select.
  selectedIndices.value = new Set();
  // Rows are re-indexed after removal and failedImages is keyed by index, so clear it.
  failedImages.value = new Set();
  deleteHover.value = false;
}
</script>

<style scoped lang="scss">
.award-group-editor {
  // Clear the page's sticky footer so the last rows aren't hidden behind it.
  padding-bottom: 6rem;

  &__table-wrap {
    overflow-x: auto;
    background: var(--palette-color-base-white);
    border: 1px solid var(--palette-color-default-lighten-90);
    border-radius: 0.5rem;
    // Contain the pinned cells' z-indexes so they can't paint over the page's
    // sticky footer (the checkbox column otherwise pokes through the buttons).
    isolation: isolate;
  }

  &__table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 0.875rem;

    th,
    td {
      padding: 0.9rem 0.75rem;
      vertical-align: middle;
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
      min-width: 11rem;
      background: var(--palette-color-base-white);
    }

    // Match the awards table header: medium weight, sentence case (no uppercase).
    th {
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--palette-color-default);
      white-space: nowrap;
    }

    tbody tr:last-child td {
      border-bottom: none;
    }

    // Keep every cell control the same height + font so the grid reads uniformly.
    // Exclude .multi-select__toggle (expands with its tags) and textareas (grow with
    // their content).
    :deep(.form-control:not(.multi-select__toggle):not(textarea)),
    :deep(.custom-select) {
      height: 2rem;
      min-height: 2rem;
      font-size: 0.875rem;
      padding: 0.2rem 0.5rem;
    }

    // bootstrap-vue draws the caret as a background image, so without reserved space on
    // the right a long option ("Service anniversaries") runs underneath it.
    :deep(.custom-select) {
      padding-right: 1.75rem;
      text-overflow: ellipsis;
    }

    // Auto-growing text fields: start at one row, grow as the text wraps.
    // bootstrap-vue sets inline `overflow-y: scroll`, which shows a permanent grey
    // scrollbar even when the text fits — override it so the bar only appears once
    // the content exceeds max-rows.
    :deep(textarea.form-control) {
      min-height: 2rem;
      font-size: 0.875rem;
      padding: 0.3rem 0.5rem;
      line-height: 1.4;
      overflow-x: hidden !important;
      overflow-y: auto !important;
    }

    :deep(.pill) {
      min-height: 2rem;
      display: inline-flex;
      align-items: center;
      font-size: 0.875rem;
    }

    :deep(.multi-select__tag) {
      font-size: 0.875rem;
    }
  }

  // Pinned columns stick to the left while the rest scroll underneath. They sit above
  // open dropdown menus (Bootstrap z-index 1000) so a menu in another column can't bleed
  // over the pinned column when scrolled. isolation: isolate on the wrap keeps these high
  // values contained so they don't leak over the page chrome.
  &__pinned {
    position: sticky;
    z-index: 1001;
    box-shadow: inset -1px 0 0 var(--palette-color-default-lighten-90);

    // The pinned column's own open dropdown must out-stack the other pinned cells so its
    // menu shows (otherwise later rows paint over it). Non-pinned dropdowns aren't lifted,
    // so they stay below the pinned column.
    &:has(.multi-select__dropdown.show) {
      z-index: 1003;
    }
  }

  // Bootstrap-vue applies a global `.dropdown-menu { z-index: 2000 }`, which would paint
  // the multiselect menus over the pinned columns. Scope the editor's menus below the
  // pinned columns (still above the ordinary scrolling cells) so the pinned columns stay
  // on top. A pinned column's own open dropdown is lifted above everything by the
  // &__pinned:has() rule above (its menu rides along inside that lifted cell).
  :deep(.multi-select__dropdown .dropdown-menu) {
    z-index: 10;
  }

  &__th {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  &__th-label {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // Pinned-column indicator: a black pin that turns purple on hover; click unpins.
  &__pin-btn {
    border: none;
    background: none;
    outline: none;
    padding: 0.125rem 0.25rem;
    cursor: pointer;
    color: var(--palette-color-default);
    flex-shrink: 0;

    &:hover {
      color: var(--palette-color-brand-primary);
    }
  }

  // The ⋮ column menu toggle (bootstrap-vue link button). Match the dark header-label
  // colour so it reads clearly; purple on hover. The theme forces solid left/right
  // borders on .btn (the bracket look), so !important is needed to strip them.
  :deep(.award-group-editor__col-menu-btn) {
    flex-shrink: 0;
    padding: 0.125rem 0.25rem;
    border: 0 !important;
    background: none !important;
    color: var(--palette-color-default);
    text-decoration: none;

    &:hover,
    &:focus,
    &:active {
      color: var(--palette-color-brand-primary);
      text-decoration: none;
      outline: none !important;
      box-shadow: none !important;
      background: none !important;
    }
  }

  // Keep the column menu compact and below the pinned column (see &__pinned z-index).
  // Drop the menu's vertical padding + clip corners so the hover highlight fills the menu
  // edge-to-edge (no white gap around the item).
  :deep(.award-group-editor__col-menu) {
    min-width: 7rem;
    z-index: 10;
    padding: 0;
    overflow: hidden;
  }

  // Nested under &__table so it out-specifies the generic `td { min-width: 11rem }`.
  // Always pinned at the very left, above other pinned columns.
  &__table &__select-col {
    left: 0;
    z-index: 1002;
    box-shadow: inset -1px 0 0 var(--palette-color-default-lighten-90);
    min-width: 2rem;
    width: 2rem;
    padding-left: 0.25rem;
    padding-right: 0.25rem;
    text-align: center;

    // The bootstrap-vue checkbox carries a default left indent for its (hidden) label;
    // remove it so the box sits centred in the narrow column.
    :deep(.custom-control) {
      padding-left: 0;
      min-height: auto;
    }
    :deep(.custom-control-label) {
      &::before,
      &::after {
        left: 50%;
        transform: translateX(-50%);
      }
    }
  }

  // Optional toggles column: fixed-width, scrolls with the rest of the table.
  &__table &__toggles-col {
    min-width: 14rem;
    width: 14rem;
    vertical-align: top;
  }

  &__toggles {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  &__toggle {
    margin: 0;
    white-space: nowrap;

    :deep(.custom-control-label) {
      font-size: 0.875rem;
      padding-top: 0.1rem;
    }
  }

  // Combined amount + cadence cell needs room for both controls on one line.
  &__budget {
    min-width: 22rem;
    flex-wrap: nowrap;

    :deep(.input-group-text) {
      font-size: 0.875rem;
      padding: 0.2rem 0.5rem;
    }

    // Let the amount and the period share the row rather than the amount eating it all.
    :deep(.form-control) {
      flex: 1 1 7rem;
      min-width: 5rem;
    }
    :deep(.custom-select) {
      flex: 1 1 9rem;
    }
  }

  &__duration {
    min-width: 9rem;

    :deep(.input-group-text) {
      font-size: 0.875rem;
      padding: 0.2rem 0.5rem;
    }
  }

  &__currency {
    min-width: 9rem;

    :deep(.input-group-text) {
      font-size: 0.875rem;
      padding: 0.2rem 0.5rem;
    }
  }

  // Delete column: sticky to the right edge, mirroring the checkbox column on the left.
  &__table &__action-col {
    position: sticky;
    right: 0;
    z-index: 1002;
    min-width: 2rem;
    width: 2rem;
    padding-left: 0.25rem;
    padding-right: 0.25rem;
    text-align: center;
    box-shadow: inset 1px 0 0 var(--palette-color-default-lighten-90);
  }

  &__delete-btn {
    color: var(--palette-color-default-lighten-40);
    padding: 0.25rem;
    font-size: 1.125rem;

    &:hover {
      color: var(--palette-color-brand-primary);
    }

    // No focus ring or grey background box on hover/click (bootstrap .btn adds them).
    &:hover,
    &:focus,
    &:active {
      outline: none !important;
      box-shadow: none !important;
      background: none !important;
    }

    // Hovering one delete in a multi-selection recolours every selected row's bin
    // (no background/box), signalling they'll all be removed together.
    &--peer-hover {
      color: var(--palette-color-brand-primary);
    }
  }

  // While 2+ rows are selected, focusing a cell accents that same column across
  // every selected row, so it's clear the edit replicates down the column. Keep a
  // thin 1px border (just recolour the existing border) rather than a thick ring.
  &__cell--accent {
    :deep(.form-control),
    :deep(.custom-select),
    :deep(.multi-select__toggle) {
      border-color: var(--palette-color-brand-primary);
    }

    // Pills read better filled than bordered, so accented edit buttons take on the
    // same filled look they show on hover.
    :deep(.edit-pill) {
      color: var(--palette-color-brand-primary);
      background: var(--palette-color-core-1-lighten-80);
      border-color: var(--palette-color-brand-primary-lighten-30);
    }
  }

  // Empty required cell after a failed submit. Recolour the existing 1px border only
  // (no ring) so it stays the same thickness as a normal field.
  &__cell--invalid {
    :deep(.form-control),
    :deep(.custom-select),
    :deep(.multi-select__toggle),
    :deep(.edit-pill) {
      border-color: var(--palette-color-danger);
    }
  }

  &__add-row {
    display: inline-flex;
    align-items: center;
    border: none;
    background: none;
    padding: 0.75rem 0.25rem;
    margin-top: 0.25rem;
    cursor: pointer;
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--palette-color-brand-primary);

    &:hover {
      text-decoration: underline;
    }
  }

  &__image-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.35rem;
  }

  &__image-actions {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__image-btn {
    padding: 0;
    font-size: 0.8125rem;
    text-align: left;
  }

  &__image-preview-wrap {
    position: relative;
    display: inline-flex;
  }

  &__image-count {
    position: absolute;
    top: -0.375rem;
    right: -0.375rem;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 1.25rem;
    height: 1.25rem;
    padding: 0 0.25rem;
    border-radius: 50%;
    background: var(--palette-color-default-lighten-50);
    color: var(--palette-color-base-white);
    font-size: 0.6875rem;
    font-weight: 600;
    line-height: 1;
    border: 2px solid var(--palette-color-base-white);
  }

  &__image-preview {
    width: 5.5rem;
    height: 3.5rem;
    border-radius: 0.5rem;
    object-fit: cover;
    border: 1px solid var(--palette-color-default-lighten-90);

    &--badge {
      width: 3.75rem;
      height: 3.75rem;
      border-radius: 50%;
    }

    // Open-award banners crop to 4:1 in the app, unlike program/milestone banners.
    &--open-banner {
      width: 6rem;
      height: 1.5rem;
    }
  }

  // Shown when an award has no (loadable) image — a dashed placeholder, rounded for
  // the banner and circular for the badge.
  &__image-placeholder {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 5.5rem;
    height: 3.5rem;
    border: 1px dashed var(--palette-color-core-1-lighten-50);
    border-radius: 0.5rem;
    background: var(--palette-color-core-1-lighten-80);
    color: var(--palette-color-core-1-lighten-30);
    font-size: 1.35rem;

    &--badge {
      width: 3.75rem;
      height: 3.75rem;
      border-radius: 50%;
    }

    &--open-banner {
      width: 6rem;
      height: 1.5rem;
      font-size: 0.9rem;
    }
  }
}
</style>
